import React from "react";
import { useNavigate } from "react-router-dom";
import "./LegalPages.css";

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <section className="legal-page">
      <button className="home-button" onClick={() => navigate("/")}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
        >
          <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      </button>

      <div className="legal-inner">
        <h1>TERMS OF SERVICE</h1>
        <p className="legal-updated">Last updated: Nov 25, 2025</p>

        <p>
          These Terms of Service (&quot;Terms&quot;) govern your use of the
          wrappr mobile application, website at www.wrappr.ai, and related
          services (collectively, the &quot;Service&quot;). By using the
          Service, you agree to these Terms. If you do not agree, you must stop
          using the Service.
        </p>

        <h2>1. ELIGIBILITY</h2>
        <p>
          You must be at least 13 years old (or the minimum digital consent age
          in your jurisdiction) to use the Service. By using wrappr, you confirm
          that you meet this requirement and that all information you provide is
          accurate.
        </p>

        <h2>2. ACCOUNTS &amp; SECURITY</h2>
        <ul>
          <li>
            Some features require an account. You agree to provide accurate and
            up-to-date information.
          </li>
          <li>
            You are responsible for maintaining the confidentiality of your
            login credentials.
          </li>
          <li>
            You must notify us immediately of any unauthorized account access.
          </li>
          <li>
            wrappr is not liable for losses resulting from unauthorized use of
            your account.
          </li>
        </ul>

        <h2>3. LICENSE TO USE THE SERVICE</h2>
        <p>
          wrappr grants you a limited, non-exclusive, non-transferable,
          revocable license to access and use the Service solely for personal or
          internal use and in compliance with these Terms.
        </p>
        <p>You may not:</p>
        <ul>
          <li>Copy, distribute, or modify any part of the Service.</li>
          <li>
            Reverse-engineer, decompile, or attempt to extract source code from
            any part of the Service.
          </li>
          <li>
            Use the Service to build a competing product or for competitive
            analysis.
          </li>
          <li>Bypass or interfere with security measures or restrictions.</li>
        </ul>

        <h2>4. USER-GENERATED CONTENT</h2>
        <p>
          You may upload or create content such as prompts, scripts, audio
          recordings, and other materials (&quot;User Content&quot;). You retain
          ownership of your User Content.
        </p>

        <p>By submitting User Content, you grant wrappr:</p>
        <ul>
          <li>
            A worldwide, non-exclusive license to process, modify, reproduce,
            and use your User Content as necessary to operate and provide the
            Service.
          </li>
          <li>
            Permission to use aggregated, anonymized, or de-identified forms of
            your User Content to improve the Service, models, and
            audio-generation capabilities and for internal analytics and
            product development.
          </li>
        </ul>

        <p>
          Where permitted by law, we may continue to use anonymized, aggregated,
          or otherwise de-identified data derived from your User Content and
          Output for internal purposes (including improving and training our
          models and services) even after you stop using the Service or delete
          your account. This data is not used to identify you personally.
        </p>

        <p>
          You represent that you have all rights necessary to submit User
          Content and that your submissions do not violate any laws or
          third-party rights.
        </p>

        <h2>5. OWNERSHIP OF AI-GENERATED OUTPUT</h2>
        <p>
          Subject to applicable law and your compliance with these Terms, you
          own the rights to the AI-generated audio and content created for you
          by the Service (&quot;Output&quot;). wrappr retains ownership of the
          underlying technology, models, and systems used to generate Output.
        </p>
        <p>
          You are solely responsible for reviewing and verifying Output before
          using or publishing it.
        </p>

        <h2>6. INTELLECTUAL PROPERTY</h2>
        <p>
          wrappr, including its software, interfaces, models, design, and all
          related intellectual property (excluding User Content and Output), is
          owned by wrappr and protected by copyright, trademark, and other
          intellectual property laws.
        </p>
        <p>You may not:</p>
        <ul>
          <li>Remove or alter copyright notices.</li>
          <li>
            Use wrappr trademarks or branding without written authorization.
          </li>
          <li>Create derivative works of the Service.</li>
        </ul>

        <h2>7. SUBSCRIPTIONS &amp; PAYMENTS</h2>
        <ul>
          <li>
            Certain features require a paid subscription. Prices are displayed
            at checkout.
          </li>
          <li>
            Payments are processed by third-party providers (e.g., Stripe or
            Apple). wrappr does not store full credit card information.
          </li>
          <li>Subscriptions renew automatically unless canceled.</li>
          <li>
            Fees are non-refundable except where required by applicable law.
          </li>
        </ul>

        <h2>8. AI-GENERATED CONTENT DISCLAIMER</h2>
        <p>
          AI-generated content may be inaccurate, incomplete, or unexpected.
          wrappr does not guarantee the accuracy, suitability, or legality of
          any Output.
        </p>
        <p>You are solely responsible for any use of Output.</p>

        <h2>9. PROHIBITED USES</h2>
        <p>You may not use the Service to:</p>
        <ul>
          <li>Generate harmful, abusive, or unlawful content.</li>
          <li>Create content that impersonates or deceives others.</li>
          <li>
            Produce fraudulent, defamatory, or misleading audio or narratives.
          </li>
          <li>Interfere with or disrupt the Service or its networks.</li>
          <li>
            Use the Service for high-risk contexts (e.g., medical, legal, or
            safety-critical applications).
          </li>
        </ul>

        <h2>10. TERMINATION &amp; ACCOUNT DELETION</h2>
        <p>
          We may suspend or terminate access to the Service at any time if we
          believe you have violated these Terms or engaged in harmful or
          unlawful conduct.
        </p>
        <p>
          You may stop using the Service at any time and you may delete your
          account directly within the app through the in-app account settings
          (for example, by using the &quot;Delete Account&quot; option in your
          profile or settings screen). When you request deletion of your
          account:
        </p>
        <ul>
          <li>
            Your account credentials and profile information are removed from
            our active systems.
          </li>
          <li>
            Where applicable, your public-facing content (such as public
            profiles or public podcasts) is unpublished or removed from
            user-facing areas of the Service.
          </li>
          <li>
            We may retain certain information in a restricted internal archive,
            including generated podcasts, usage logs, and technical records, for
            legitimate business purposes such as security, fraud prevention,
            legal compliance, and internal analytics.
          </li>
          <li>
            Archived data is not available to other users, is not used to
            recreate or restore your account, and is not sold or shared with
            third parties for their own marketing purposes.
          </li>
          <li>
            Anonymized, aggregated, or de-identified data derived from your use
            of the Service may be retained and used internally to improve the
            Service, including training and evaluating our models and
            audio-generation capabilities.
          </li>
        </ul>
        <p>
          Most deletions are processed promptly, but certain backups or archives
          may take additional time to fully purge due to technical and legal
          requirements.
        </p>
        <p>
          If you have questions about account deletion or would like to request
          additional deletion of archived data where legally and technically
          possible, you may contact us at{" "}
          <a href="mailto:hello@wrappr.ai">hello@wrappr.ai</a>.
        </p>
        <p>
          Sections that reasonably should survive termination (including
          intellectual property, warranties, limitations of liability, and
          indemnity) remain in effect after your use of the Service ends or your
          account is deleted.
        </p>

        <h2>11. INDEMNIFICATION</h2>
        <p>
          You agree to indemnify, defend, and hold harmless wrappr and its
          affiliates from any claims, damages, losses, liabilities, and expenses
          arising from:
        </p>
        <ul>
          <li>Your use of the Service</li>
          <li>Your User Content or Output</li>
          <li>Your violation of these Terms</li>
          <li>Your infringement of third-party rights</li>
        </ul>

        <h2>12. DISCLAIMER OF WARRANTIES</h2>
        <p>
          The Service is provided on an &quot;AS IS&quot; and &quot;AS
          AVAILABLE&quot; basis. wrappr makes no warranties, express or implied,
          including merchantability, fitness for a particular purpose,
          availability, accuracy, or non-infringement.
        </p>

        <h2>13. LIMITATION OF LIABILITY</h2>
        <p>
          To the fullest extent permitted by law, wrappr is not liable for any
          indirect, incidental, special, consequential, or punitive damages,
          including loss of data, content, revenue, or profits.
        </p>
        <p>
          wrappr&apos;s total liability shall not exceed the amount you paid for
          the Service, if any, in the six months preceding the claim.
        </p>

        <h2>14. THIRD-PARTY SERVICES</h2>
        <p>
          The Service may integrate with third-party APIs, hosting providers,
          AI/ML services, or external tools. wrappr is not responsible for the
          availability, accuracy, or practices of third-party services.
        </p>

        <h2>15. MODIFICATIONS TO THE SERVICE</h2>
        <p>
          wrappr may update, modify, or discontinue any part of the Service at
          any time. We may provide notice where required by law.
        </p>

        <h2>16. CHANGES TO THESE TERMS</h2>
        <p>
          We may revise these Terms. The updated &quot;Last Updated&quot; date
          reflects the version in effect. Continued use of the Service after
          changes indicates acceptance of the updated Terms.
        </p>

        <h2>17. GOVERNING LAW &amp; DISPUTE RESOLUTION</h2>
        <p>
          These Terms are governed by the laws of the State of California,
          excluding its conflict-of-law rules. Any disputes must be resolved in
          the state or federal courts located in San Francisco County,
          California, unless otherwise required by law.
        </p>

        <h2>18. ADDITIONAL TERMS FOR iOS USERS</h2>
        <p>
          Apple is not responsible for the maintenance or support of the
          Service. Your use must comply with Apple&apos;s App Store Terms and
          Conditions. Apple and its subsidiaries are third-party beneficiaries
          of these Terms.
        </p>

        <h2>19. CONTACT US</h2>
        <p>
          If you have any questions about these Terms, contact us at:
          <br />
          <strong>wrappr</strong>
          <br />
          Email: <a href="mailto:hello@wrappr.ai">hello@wrappr.ai</a>
        </p>
      </div>
    </section>
  );
};

export default TermsOfService;
